import { CONTACT_EMAIL } from "./helpCenterContent";

export const COMPANY_PAGES = {
  about: {
    id: "about",
    eyebrow: "Company",
    title: "About",
    titleAccent: "PagePeers",
    intro:
      "We build a calm home for readers — shelves, circles, and discovery without the noise of traditional social feeds.",
    sections: [
      {
        heading: "Our story",
        paragraphs: [
          "PagePeers began with a simple observation: readers already have rich lives around books, but most apps treat reading like a race. We wanted software that feels like a reading room — warm light, unhurried conversation, and space to change your mind.",
          "Today, PagePeers Club helps members track shelves, lend books, join reader circles, and share recommendations with people they trust. The public site introduces new readers to curated shelves, guides, and the community waiting on the other side of the bookmark.",
        ],
      },
      {
        heading: "What we believe",
        list: [
          "Reading is personal — progress without guilt.",
          "Community works best in small groups with clear norms.",
          "Design should disappear while you read.",
          "Discovery through friends beats algorithmic feeds.",
        ],
      },
      {
        heading: "The team",
        paragraphs: [
          "We are a small team of readers, designers, and engineers spread across time zones but united by paperback piles and too many tabs. We ship carefully, listen to circles, and measure success in finished chapters — not daily active anxiety.",
        ],
      },
    ],
    cta: {
      title: "Join the reading room",
      desc: "Create a free account, build your shelf, and find a circle that fits your pace.",
      primaryHref: "https://club.pagepeers.com/register",
      primaryLabel: "Join PagePeers Club",
      secondaryTo: "/how-it-works",
      secondaryLabel: "How it works",
    },
  },
  journal: {
    id: "journal",
    eyebrow: "Editorial",
    title: "The PagePeers",
    titleAccent: "journal",
    intro:
      "Essays, shelf notes, and quiet observations from the people building and reading on PagePeers.",
    sections: [
      {
        heading: "What lives here",
        paragraphs: [
          "The journal is our editorial home — longer reflections on reading culture, product craft, and the habits that keep book lovers returning to the page. For weekly roundups, reviews, and circle highlights, visit the Book Blog.",
        ],
      },
    ],
    cards: [
      {
        title: "Book Blog",
        desc: "Reviews, vlog notes, and community roundups — updated regularly.",
      },
      {
        title: "Reading Guides",
        desc: "Practical essays on shelves, circles, and calm reading habits.",
      },
      {
        title: "Curated Shelves",
        desc: "Themed collections with a curator’s voice and room to linger.",
      },
    ],
    cta: {
      title: "Read the latest",
      desc: "Browse recent posts from the PagePeers reading room.",
      primaryTo: "/book-blog",
      primaryLabel: "Open Book Blog",
      secondaryTo: "/reading-guides",
      secondaryLabel: "Reading guides",
    },
  },
  careers: {
    id: "careers",
    eyebrow: "Careers",
    title: "Work with",
    titleAccent: "readers",
    intro:
      "We hire people who care about craft, kindness, and books. Remote-friendly roles with async collaboration and deep respect for focus time.",
    sections: [
      {
        heading: "How we work",
        paragraphs: [
          "PagePeers is built by a distributed team that defaults to trust. We write clearly, ship in small slices, and protect uninterrupted reading — including our own.",
        ],
        list: [
          "Remote-first with flexible hours",
          "Thoughtful benefits and paid time off",
          "Learning budget for books and conferences",
          "No performative hustle culture",
        ],
      },
      {
        heading: "Open roles",
        paragraphs: [
          "We do not have public openings listed right now. When we hire, roles typically span product engineering, design, community operations, and editorial.",
          `Interested anyway? Send your CV, a short note, and one book that changed how you read to ${CONTACT_EMAIL} with the subject line “Careers at PagePeers”.`,
        ],
      },
    ],
    cta: {
      title: "Say hello",
      desc: "Tell us what you would build for readers and why PagePeers feels like your kind of project.",
      primaryHref: "mailto:support@pagepeers.com?subject=Careers%20at%20PagePeers",
      primaryLabel: "Email careers",
      secondaryTo: "/about",
      secondaryLabel: "About us",
    },
  },
  "press-kit": {
    id: "press-kit",
    eyebrow: "Press",
    title: "Press",
    titleAccent: "kit",
    intro:
      "Facts, positioning, and contacts for journalists, podcasters, and partners covering PagePeers.",
    sections: [
      {
        heading: "Boilerplate",
        paragraphs: [
          "PagePeers is a reader community platform where members organize personal shelves, join small discussion circles, and discover books through curated collections and trusted recommendations. PagePeers Club offers lending, reviews, and membership tools; the public site showcases shelves, guides, and editorial content for new readers.",
        ],
      },
      {
        heading: "Quick facts",
        list: [
          "Product: PagePeers Club (member app) + pagepeers.com (public)",
          "Focus: calm reading tools and small-group community",
          "Founded: reader-led product team, global contributors",
          "Contact: support@pagepeers.com for press inquiries",
        ],
      },
      {
        heading: "Brand voice",
        paragraphs: [
          "Warm, literary, and unhurried. We avoid hustle language and speak to readers as friends at a kitchen table — never as metrics to optimize.",
        ],
      },
      {
        heading: "Assets & interviews",
        paragraphs: [
          "Logo files and founder photos are available on request. For interviews, product briefings, or embargoed announcements, email our team and we will respond within two business days.",
        ],
      },
    ],
    cta: {
      title: "Press contact",
      desc: "Reach the PagePeers team for media requests and partnership conversations.",
      primaryHref: `mailto:${CONTACT_EMAIL}?subject=Press%20inquiry`,
      primaryLabel: "Contact press",
      secondaryTo: "/help-center",
      secondaryLabel: "Help center",
    },
  },
};
